import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, PencilSimple, Trash, Printer, Wallet, ImageSquare, PenNib, X, Copy, CopySimple, CircleNotch, Barcode, WarningCircle, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { downloadBlob, blobError, openPdfForPrint, preparePrintWindow, printBlobInPage } from "../lib/download";
import { fmtBRL, fmtDate, todayISO, maskDocument, PAYMENT_METHODS, ORDER_STATUSES } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { EmitBoletoDialog } from "../components/EmitBoletoDialog";
import { ClientSearchInput } from "../components/ClientSearchInput";
import { ProductSearchInput } from "../components/ProductSearchInput";
import { DatePicker } from "../components/DatePicker";
import { OrderPaymentsDialog } from "../components/OrderPaymentsDialog";
import { SignatureDialog } from "../components/SignaturePad";
import { DocumentPreviewDialog } from "../components/DocumentPreviewDialog";
import { OrderCreatedDialog } from "../components/OrderCreatedDialog";
import { DuplicateOrdersDialog } from "../components/DuplicateOrdersDialog";
import { NfeDialog } from "../components/nfe/NfeDialog";
import { PendingOrdersPanel } from "../components/PendingOrdersPanel";
import { ZeroCostBanner, ZeroCostItemWarning, QuickCostDialog } from "../components/ZeroCostAlert";
import { useRowSwipe } from "../hooks/useRowSwipe";
import { useOfflineOrderSync } from "../hooks/useOfflineOrderSync";
import { useIsDesktop, useDebouncedValue } from "../hooks/useResponsiveList";
import { Pagination } from "../components/Pagination";
import { cacheClients, getCachedClients, enqueueOrder } from "../lib/offlineOrders";
import { loadViewCache, saveViewCache } from "../lib/viewCache";
import { buildPlan } from "../lib/schedule";
import { OrderPaymentPlan } from "../components/OrderPaymentPlan";
import { OrderItemsSummary } from "../components/OrderItemsSummary";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const input = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const pInput = "w-full bg-white border border-slate-300 rounded px-2 py-2.5 sm:py-1.5 text-base sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-800";
const pLabel = "text-[11px] font-semibold uppercase tracking-wide text-blue-950 shrink-0";

const genRequestId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const emptyItem = () => ({ _key: Math.random().toString(36).slice(2), quant: 1, description: "", unit_price: "", product_id: null, internal: false });
const emptyForm = () => ({
  client_id: "", nf_number: "", address: "", city: "", state: "", document: "", notes: "",
  order_date: todayISO(), status: "Em processamento", payment_method: "PIX",
  installments: 1, payment_status: "Pendente", items: [emptyItem()], discount: "", freight: "", boleto_days: "", commission_percent: "0",
  entrada_value: "", entrada_method: "PIX", due_dates: [],
  client_request_id: genRequestId(),
});

const itemTotal = (it) => (parseFloat(it.quant) || 0) * (parseFloat(it.unit_price) || 0);
const PAGE_SIZE = 50;

// Status Pgto: quando o pedido tem boleto em carteira vencido, destaca "Boleto vencido · N dias" (status real embaixo)
const PayStatus = ({ o, testId }) => {
  const bv = o.boleto_vencido;
  if (!bv) return <StatusBadge status={o.payment_status || "Pendente"} testId={testId} />;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span data-testid={testId} title={`${bv.count} boleto(s) em carteira vencido(s) — ${bv.total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} em atraso`}
        className="inline-flex items-center whitespace-nowrap px-2 py-0.5 text-xs font-bold border rounded text-rose-300 bg-rose-500/15 border-rose-500/40">
        Boleto vencido · {bv.dias}d
      </span>
      <span className="text-[10px] text-zinc-500">{o.payment_status || "Pendente"}</span>
    </span>
  );
};


export default function Orders() {
  const { user } = useAuth();
  const isVendedor = user?.role === "vendedor";
  const isDesktop = useIsDesktop();
  const [orders, setOrders] = useState(() => loadViewCache("orders", user?.id) || []);
  const [total, setTotal] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterVendedor, setFilterVendedor] = useState("");
  const [filterPayStatus, setFilterPayStatus] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [onlyInternal, setOnlyInternal] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [payOrder, setPayOrder] = useState(null);
  const [signOrder, setSignOrder] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [nfeOrder, setNfeOrder] = useState(null);
  const [boletoOrder, setBoletoOrder] = useState(null);
  const [descAudit, setDescAudit] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());
  const toggleItems = (id) => setExpandedItems((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [batchBusy, setBatchBusy] = useState(false);
  const [costProduct, setCostProduct] = useState(null);
  const swipe = useRowSwipe((id) => {
    const o = orders.find((x) => x.id === id);
    if (o) setPayOrder(o);
  }, !isVendedor);

  const zeroCostProducts = (() => {
    const seen = new Map();
    form.items.forEach((it) => {
      if (!it.product_id || seen.has(it.product_id)) return;
      const p = products.find((x) => x.id === it.product_id);
      if (p && p.has_cost === false) seen.set(p.id, p);
    });
    return [...seen.values()];
  })();
  const onCostSaved = (pid, avgCost) =>
    setProducts((list) => list.map((p) => (p.id === pid ? { ...p, avg_cost: avgCost, has_cost: true } : p)));

  const subtotal = form.items.reduce((s, it) => s + itemTotal(it), 0);
  const discountVal = Math.min(Math.max(parseFloat(form.discount) || 0, 0), subtotal);
  const freightVal = Math.max(parseFloat(form.freight) || 0, 0);
  const grandTotal = Math.max(subtotal - discountVal + freightVal, 0);
  const plan = buildPlan({ total: grandTotal, installments: form.installments, payment_method: form.payment_method,
    boleto_days: form.boleto_days, order_date: form.order_date, entrada_value: form.entrada_value, due_dates: form.due_dates });

  const load = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (filterClient) params.client_id = filterClient;
    if (filterVendedor) params.vendedor_id = filterVendedor;
    if (filterPayStatus === "boleto_vencido") params.boleto_vencido = 1;
    else if (filterPayStatus) params.payment_status = filterPayStatus;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (onlyInternal) params.internal_only = 1;
    const unfiltered = Object.keys(params).length === 2 && page === 1;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    api.get("/orders", { params, signal: controller.signal })
      .then((r) => {
        const items = r.data.items || [];
        setOrders(items);
        setTotal(r.data.total || 0);
        setTotalValue(r.data.total_value_sum || 0);
        if (unfiltered) saveViewCache("orders", user?.id, items);
        if (user?.role !== "vendedor" && items.length) {
          api.get("/orders/description-audit", { params: { ids: items.map((o) => o.id).join(",") } })
            .then((d) => setDescAudit(d.data)).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => { if (abortRef.current === controller) setLoading(false); });
  }, [page, filterClient, filterVendedor, filterPayStatus, debouncedSearch, dateFrom, dateTo, onlyInternal, user?.id, user?.role]);

  useEffect(() => { load(); }, [load]);
  // qualquer mudança de filtro/busca volta para a primeira página
  useEffect(() => { setPage(1); }, [filterClient, filterVendedor, filterPayStatus, debouncedSearch, dateFrom, dateTo, onlyInternal]);

  const fullOrder = async (o) => {
    try { return (await api.get(`/orders/${o.id}`)).data; } catch { return o; }
  };
  const openPreview = async (o) => setPreviewOrder(await fullOrder(o));

  const toggleBoletoSent = async (o) => {
    try {
      await api.patch(`/orders/${o.id}/boleto-sent`, { sent: !o.boleto_sent });
      toast.success(!o.boleto_sent
        ? `Pedido #${o.code}: boleto marcado como enviado`
        : `Pedido #${o.code}: boleto marcado como NÃO enviado — voltou para as pendências`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => {
    api.get("/clients")
      .then((r) => { setClients(r.data); cacheClients(r.data); })
      .catch(() => { const cached = getCachedClients(); if (cached.length) setClients(cached); });
    api.get("/products", { params: { only_active: 1 } }).then((r) => setProducts(r.data)).catch(() => {});
    if (user?.role !== "vendedor") api.get("/users/vendedores").then((r) => setVendedores(r.data)).catch(() => {});
  }, [user?.role]);
  const syncNow = useOfflineOrderSync(load);

  const formRef = useRef(null);
  const setItem = (idx, field, value) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)) }));
  const setItemDesc = (idx, text, productId = null) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, description: text, product_id: productId } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const toggleInternal = (idx, checked) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, internal: checked } : it)) }));

  const domDesc = (idx) => {
    const els = formRef.current?.querySelectorAll(`[data-desc-idx="${idx}"]`) || [];
    for (const el of els) { if (el.value && el.value.trim()) return el.value.trim(); }
    return "";
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.client_id) {
      toast.error("Selecione um cliente");
      return;
    }
    const mapped = form.items.map((it, idx) => ({
      quant: parseFloat(it.quant) || 0,
      description: (it.description || "").trim() || domDesc(idx),
      unit_price: parseFloat(it.unit_price) || 0,
      product_id: it.product_id || null,
      internal: !!it.internal,
    }));
    const noDesc = mapped.findIndex((it) => it.quant * it.unit_price > 0 && !it.description);
    if (noDesc !== -1) {
      toast.error(`Informe a descrição do produto no item ${noDesc + 1} antes de salvar`);
      return;
    }
    const items = mapped.filter((it) => it.description || it.quant * it.unit_price > 0);
    if (items.length === 0) {
      toast.error("Adicione ao menos um item ao pedido");
      return;
    }
    if (plan.error) {
      toast.error(plan.error);
      return;
    }
    const payload = { ...form, items, installments: parseInt(form.installments) || 1,
      discount: parseFloat(form.discount) || 0,
      freight_value: parseFloat(form.freight) || 0,
      commission_percent: parseFloat(form.commission_percent) || 0,
      boleto_days: form.payment_method === "Boleto" ? parseInt(form.boleto_days) || 0 : 0,
      entrada_value: parseFloat(form.entrada_value) || 0,
      entrada_method: form.entrada_method || "PIX",
      installment_due_dates: plan.hasCustom ? plan.parcelas.map((p) => p.due_date) : [],
      description: items.filter((it) => !it.internal).map((it) => `${it.quant}x ${it.description}`).join("; ") };
    delete payload.due_dates;
    const clientName = clients.find((c) => c.id === form.client_id)?.name || "";

    if (!editing && !navigator.onLine) {
      enqueueOrder(payload, clientName);
      toast.success("Sem conexão — pedido salvo no aparelho e será enviado automaticamente quando a internet voltar");
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/orders/${editing}`, payload);
        if (data.stock_warnings?.length) {
          toast.warning(`Atenção ao estoque: ${data.stock_warnings.join(" · ")}`, { duration: 9000 });
        }
        if (data.margin_warnings?.length) {
          toast.warning(`Venda abaixo do custo: ${data.margin_warnings.join(" · ")}`, { duration: 12000 });
        }
        toast.success("Pedido atualizado");
      } else {
        try {
          const { data: cr } = await api.get(`/clients/${form.client_id}/credit`);
          if (cr.credit > 0) {
            payload.use_credit = window.confirm(
              `Este cliente tem ${fmtBRL(cr.credit)} de crédito disponível.\n\nOK = abater automaticamente deste pedido.\nCancelar = NÃO aplicar crédito neste pedido (o crédito permanece disponível para aplicação manual pelo Financeiro).`);
          }
        } catch (err) {
          console.error("Não foi possível verificar o crédito do cliente; seguindo sem abatimento automático:", err);
        }
        const { data } = await api.post("/orders", payload);
        if (data.stock_warnings?.length) {
          toast.warning(`Atenção ao estoque: ${data.stock_warnings.join(" · ")}`, { duration: 9000 });
        }
        if (data.margin_warnings?.length) {
          toast.warning(`Venda abaixo do custo: ${data.margin_warnings.join(" · ")}`, { duration: 12000 });
        }
        setCreated({ code: data.code, client_name: clientName, total_value: data.total_value,
          installments: payload.installments, credit_applied: data.credit_applied || 0,
          already_existed: data.already_existed });
      }
      setOpen(false);
      load();
    } catch (err) {
      if (!editing && !err?.response) {
        enqueueOrder(payload, clientName);
        toast.success("Conexão instável — pedido salvo no aparelho e será enviado automaticamente quando possível");
        setOpen(false);
        return;
      }
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este pedido? Os lançamentos financeiros vinculados também serão removidos.")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Pedido excluído");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const printPdf = async (o, letterhead = false) => {
    try {
      const res = await api.get(`/orders/${o.id}/pdf`, { params: { letterhead: letterhead ? 1 : 0 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedido_${o.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Talão do pedido #${o.code} gerado em PDF`);
    } catch (err) {
      toast.error("Erro ao gerar PDF do talão");
    }
  };

  const openNew = () => { setForm(emptyForm()); setEditing(null); setEditingCode(null); setOpen(true); };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = orders.length > 0 && orders.every((o) => selected.has(o.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) orders.forEach((o) => next.delete(o.id));
      else orders.forEach((o) => next.add(o.id));
      return next;
    });
  };

  const printBatch = async (letterhead = false, vias = 1) => {
    const ids = [...selected];
    if (ids.length === 0) { toast.error("Selecione ao menos um pedido"); return; }
    if (ids.length > 50) { toast.error("Máximo de 50 pedidos por lote — desmarque alguns pedidos"); return; }
    setBatchBusy(true);
    if (vias === 2) {
      try {
        const res = await api.get("/orders/batch-pdf",
          { params: { ids: ids.join(","), letterhead: 0, vias: 2 }, responseType: "blob" });
        downloadBlob(res.data, `pedidos_lote_${ids.length}_2vias.pdf`);
        toast.success(`Lote de ${ids.length} pedido(s) em 2 vias baixado — abra o arquivo e imprima (1 folha por pedido)`);
      } catch (err) {
        toast.error(await blobError(err, "Erro ao gerar o lote em 2 vias — tente novamente"));
      } finally {
        setBatchBusy(false);
      }
      return;
    }
    const win = window.open("", "_blank");
    preparePrintWindow(win, "Gerando PDF do lote — aguarde...");
    try {
      const res = await api.get("/orders/batch-pdf",
        { params: { ids: ids.join(","), letterhead: letterhead ? 1 : 0 }, responseType: "blob" });
      if (win) {
        const url = URL.createObjectURL(res.data);
        openPdfForPrint(win, url, `Lote de ${ids.length} pedido(s)`);
      } else {
        printBlobInPage(res.data, `pedidos_lote_${ids.length}.pdf`);
        toast.info("Pop-up bloqueado pelo navegador — abrindo a impressão aqui mesmo. Permita pop-ups do site para abrir em nova aba.");
      }
      downloadBlob(res.data, `pedidos_lote_${ids.length}.pdf`);
      toast.success(`PDF com ${ids.length} pedido(s) aberto para impressão e baixado`);
    } catch (err) {
      if (win) win.close();
      toast.error(await blobError(err, "Erro ao gerar o PDF do lote — tente novamente"));
    } finally {
      setBatchBusy(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("novo") === "1") {
      openNew();
      searchParams.delete("novo");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openEdit = async (row) => {
    const o = await fullOrder(row);
    setForm({
      client_id: o.client_id, nf_number: o.nf_number || "",
      address: o.address || "", city: o.city || "", state: o.state || "",
      document: o.document || "", notes: o.notes || "",
      order_date: o.order_date, status: o.status,
      payment_method: o.payment_method, installments: o.installments,
      payment_status: o.payment_status || "Pendente",
      discount: o.discount ? String(o.discount) : "",
      freight: o.freight_value ? String(o.freight_value) : "",
      commission_percent: o.commission_percent ? String(o.commission_percent) : "0",
      boleto_days: o.boleto_days ? String(o.boleto_days) : "",
      entrada_value: o.entrada_value ? String(o.entrada_value) : "", entrada_method: o.entrada_method || "PIX",
      due_dates: o.installment_due_dates || [],
      items: o.items && o.items.length > 0
        ? o.items.map((it) => ({ _key: Math.random().toString(36).slice(2), quant: it.quant, description: it.description, unit_price: it.unit_price, product_id: it.product_id || null, internal: !!it.internal }))
        : [{ _key: Math.random().toString(36).slice(2), quant: 1, description: (o.description || "Item").replace(/^\d+(?:[.,]\d+)?x\s+/i, ""), unit_price: o.total_value, product_id: null, internal: false }],
    });
    setEditing(o.id);
    setEditingCode(o.code);
    setOpen(true);
  };

  return (
    <div data-testid="orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">Pedidos</h1>
          <p className="text-sm text-zinc-500">Ao criar um pedido, os recebimentos são gerados automaticamente</p>
        </div>
        <div className="flex items-center gap-2">
          {!isVendedor && (
            <button data-testid="duplicates-report-button" onClick={() => setDupOpen(true)}
              className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm font-medium px-4 py-3 min-h-[44px] rounded-lg transition-colors">
              <Copy size={16} /> <span className="hidden sm:inline">Duplicados</span>
            </button>
          )}
          <button data-testid="add-order-button" onClick={openNew}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] text-white text-sm font-medium px-5 py-3 min-h-[44px] rounded-lg transition-all">
            <Plus size={18} weight="bold" /> Novo Pedido
          </button>
        </div>
      </div>

      <div className="mb-4"><PendingOrdersPanel onSync={syncNow} /></div>

      {!isVendedor && descAudit?.count > 0 && (
        <div data-testid="desc-audit-banner" className="mb-4 bg-amber-950/40 border border-amber-800/60 rounded-lg px-4 py-3">
          <p className="text-sm text-amber-200 font-medium mb-2">
            {descAudit.count} pedido(s) com item sem descrição de produto — toque no pedido para corrigir:
          </p>
          <div className="flex flex-wrap gap-2">
            {descAudit.orders.map((o) => (
              <button key={o.id} type="button" data-testid={`desc-audit-order-${o.code}`} onClick={() => openEdit(o)}
                className="text-xs bg-amber-900/50 hover:bg-amber-800/60 active:scale-[0.98] border border-amber-700/50 rounded px-2.5 py-1.5 text-amber-100 transition-all">
                #{o.code} · {o.client_name} · {fmtDate(o.order_date)} · {fmtBRL(o.total_value)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div data-testid="orders-filters" className="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-30 bg-zinc-950/95 backdrop-blur -mx-4 px-4 py-3 mb-4 border-b border-zinc-800/60 lg:relative lg:top-auto lg:z-20 lg:backdrop-blur-none lg:mx-0 lg:px-0 lg:py-0 lg:bg-transparent lg:border-0 flex flex-wrap items-center gap-3">
        <ClientSearchInput testId="order-filter-client" className="w-full max-w-sm" clients={clients}
          value={filterClient} onChange={setFilterClient} />
        <input data-testid="order-search-input" className={`${input} max-w-sm`}
          placeholder="Buscar por cliente, produto, nº NF ou nº do pedido..." value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <DatePicker testId="order-date-from" value={dateFrom} onChange={setDateFrom} placeholder="De" clearable />
        <DatePicker testId="order-date-to" value={dateTo} onChange={setDateTo} placeholder="Até" clearable />
        <select data-testid="order-filter-pay-status" className={`${input} max-w-[180px]`} value={filterPayStatus}
          onChange={(e) => setFilterPayStatus(e.target.value)}>
          <option value="">Status pgto: todos</option>
          <option value="Pendente">Pendente</option>
          <option value="Pago Parcialmente">Pago Parcialmente</option>
          <option value="Pago">Pago</option>
          {!isVendedor && <option value="boleto_vencido">Boleto vencido</option>}
        </select>
        {!isVendedor && vendedores.length > 0 && (
          <select data-testid="order-filter-vendedor" className={`${input} max-w-[200px]`} value={filterVendedor}
            onChange={(e) => setFilterVendedor(e.target.value)}>
            <option value="">Vendedor: todos</option>
            {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
        {!isVendedor && (
          <label data-testid="orders-internal-filter" className={`flex items-center gap-2 text-sm cursor-pointer select-none rounded-lg px-3 py-2 border transition-colors ${onlyInternal ? "border-violet-600 bg-violet-950/50 text-violet-200" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
            <input type="checkbox" className="accent-violet-500 w-4 h-4" checked={onlyInternal}
              onChange={(e) => setOnlyInternal(e.target.checked)} />
            Com itens internos
          </label>
        )}
        {loading && <CircleNotch size={18} className="animate-spin text-indigo-400" data-testid="orders-loading" />}
        {(dateFrom || dateTo || search || filterClient || onlyInternal || filterVendedor || filterPayStatus) && (
          <span data-testid="orders-filter-summary" className="text-sm text-zinc-400">
            {total.toLocaleString("pt-BR")} pedido(s) · Total <span className="text-emerald-400 font-medium">{fmtBRL(totalValue)}</span>
          </span>
        )}
      </div>

      {selected.size > 0 && (
        <div data-testid="batch-actions-bar" className="flex flex-wrap items-center gap-3 mb-4 bg-indigo-950/40 border border-indigo-800/50 rounded-lg px-4 py-3">
          <span data-testid="batch-selected-count" className="text-sm text-indigo-200 font-medium">
            {selected.size} pedido(s) selecionado(s)
          </span>
          <button data-testid="batch-print-button" onClick={() => printBatch(false)} disabled={batchBusy}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Printer size={16} /> {batchBusy ? "Gerando..." : "Imprimir lote"}
          </button>
          <button data-testid="batch-print-2vias-button" onClick={() => printBatch(false, 2)} disabled={batchBusy}
            title="Imprime cada pedido do lote com a via duplicada na folha (cliente + canhoto)"
            className="flex items-center gap-2 bg-teal-700 hover:bg-teal-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <CopySimple size={16} /> Lote 2 vias
          </button>
          <button data-testid="batch-print-logo-button" onClick={() => printBatch(true)} disabled={batchBusy}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <ImageSquare size={16} /> Lote com logotipo
          </button>
          <button data-testid="batch-clear-button" onClick={() => setSelected(new Set())} disabled={batchBusy}
            className="flex items-center gap-1 text-zinc-400 hover:text-zinc-200 text-sm px-2 py-2 transition-colors">
            <X size={14} /> Limpar seleção
          </button>
        </div>
      )}

      {/* Cards mobile — renderizados SÓ no celular (nunca junto com a tabela) */}
      {!isDesktop && (
      <div className="space-y-3" data-testid="orders-cards">
        {orders.length === 0 && <p className="text-center text-zinc-500 py-8">Nenhum pedido encontrado.</p>}
        {orders.map((o) => (
          <div key={o.id} data-testid={`order-card-${o.code}`} className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-2.5">
                <input type="checkbox" data-testid={`m-select-order-${o.code}`} checked={selected.has(o.id)}
                  onChange={() => toggleSelect(o.id)} className="accent-indigo-500 w-5 h-5" />
                <span className="text-zinc-500 text-sm">#{o.code}</span>
              </label>
              <PayStatus o={o} testId={`m-order-pay-status-${o.code}`} />
            </div>
            <div data-testid={`m-preview-order-${o.code}`} onClick={() => openPreview(o)} className="cursor-pointer active:opacity-70 transition-opacity">
              <div>
                <p className="text-zinc-100 font-medium">{o.client_name}
                  {!isVendedor && o.items?.some((it) => it.internal) && (
                    <span data-testid={`m-order-internal-badge-${o.code}`} className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-violet-950/60 text-violet-300 border border-violet-800/60 rounded px-1.5 py-0.5 align-middle">Interno</span>
                  )}
                </p>
                {o.vendedor_name && (
                  <p data-testid={`m-order-vendedor-${o.code}`} className="text-xs text-indigo-300/90 mt-0.5">Vendedor: {o.vendedor_name}</p>
                )}
                <p className="text-xs text-zinc-500 mt-0.5">
                  {fmtDate(o.order_date)} · {o.payment_method}
                  {o.payment_method === "Boleto" && o.boleto_days > 0 && ` (${o.boleto_days} dias)`} · {o.installments}x{o.entrada_value > 0 && <span className="text-amber-400"> + entrada {fmtBRL(o.entrada_value)}</span>}
                  {o.nf_number && ` · NF ${o.nf_number}`}
                </p>
                <OrderItemsSummary order={o} isVendedor={isVendedor} compact max={2} testPrefix="m-" expanded={expandedItems.has(o.id)} onToggle={() => toggleItems(o.id)} />
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-display text-lg font-semibold">{fmtBRL(o.total_value)}</span>
                <StatusBadge status={o.status} />
              </div>
            </div>
            <div className="flex gap-2 border-t border-zinc-800/60 pt-2.5">
              {!isVendedor && (
                <button data-testid={`m-payments-order-${o.code}`} onClick={() => setPayOrder(o)} title="Histórico de pagamentos" aria-label="Histórico de pagamentos"
                  className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-amber-400 active:bg-zinc-800 transition-colors"><Wallet size={19} /></button>
              )}
              <button data-testid={`m-signature-order-${o.code}`} onClick={() => setSignOrder(o)} title={o.has_signature ? "Ver assinatura" : "Coletar assinatura"} aria-label="Assinatura de recebimento"
                className={`flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 active:bg-zinc-800 transition-colors ${o.has_signature ? "text-emerald-400" : "text-zinc-400"}`}>
                <PenNib size={19} weight={o.has_signature ? "fill" : "regular"} /></button>
              {!isVendedor && o.payment_method === "Boleto" && o.payment_status !== "Pago" && (
                <button data-testid={`m-boletos-order-${o.code}`} onClick={() => setBoletoOrder(o)} title="Emitir boletos Sicredi das parcelas em aberto" aria-label="Emitir boletos Sicredi"
                  className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-emerald-400 active:bg-zinc-800 transition-colors"><Receipt size={19} /></button>
              )}
              {!isVendedor && (
                <button data-testid={`m-nfe-order-${o.code}`} onClick={() => setNfeOrder(o)} title="NF-e / DANFE" aria-label="NF-e / DANFE"
                  className="flex-1 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-sky-400 active:bg-zinc-800 transition-colors"><Barcode size={19} /></button>
              )}
              <button data-testid={`m-print-order-logo-${o.code}`} onClick={() => printPdf(o, true)} title="PDF com logotipo" aria-label="PDF com logotipo"
                className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-amber-400 active:bg-zinc-800 transition-colors"><ImageSquare size={19} /></button>
              <button data-testid={`m-print-order-${o.code}`} onClick={() => printPdf(o)} title="Imprimir talão (PDF)" aria-label="Imprimir talão"
                className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-emerald-400 active:bg-zinc-800 transition-colors"><Printer size={19} /></button>
              <button data-testid={`m-edit-order-${o.code}`} onClick={() => openEdit(o)} title="Editar pedido" aria-label="Editar pedido"
                className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-indigo-400 active:bg-zinc-800 transition-colors"><PencilSimple size={19} /></button>
              <button data-testid={`m-delete-order-${o.code}`} onClick={() => remove(o.id)} title="Excluir pedido" aria-label="Excluir pedido"
                className="flex-1 min-h-[44px] flex items-center justify-center rounded-lg border border-zinc-800 text-rose-400 active:bg-zinc-800 transition-colors"><Trash size={19} /></button>
            </div>
          </div>
        ))}
      </div>
      )}

      {isDesktop && (
      <div className="border border-zinc-800 rounded-lg overflow-x-auto" data-testid="orders-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.1em] text-zinc-500 border-b border-zinc-800">
              <th className="px-3 py-3 w-[44px] min-w-[44px] sticky-col-l">
                <input type="checkbox" data-testid="select-all-orders" checked={allVisibleSelected}
                  onChange={toggleSelectAll} title="Selecionar todos os pedidos listados"
                  className="accent-indigo-500 w-4 h-4 cursor-pointer align-middle" />
              </th>
              <th className="px-4 py-3 font-medium sticky-col-l2">ID</th>
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Vendedor</th>
              <th className="px-4 py-3 font-medium">Valor Total</th>
              {!isVendedor && <th className="px-4 py-3 font-medium">Margem</th>}
              <th className="px-4 py-3 font-medium">NF/Pedido</th>
              <th className="px-4 py-3 font-medium">Data</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Pagamento</th>
              <th className="px-4 py-3 font-medium">Parcelas</th>
              <th className="px-4 py-3 font-medium">Status Pgto</th>
              <th className="px-4 py-3 font-medium text-right sticky-col-r">Ações</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={isVendedor ? 12 : 13} className="px-4 py-8 text-center text-zinc-500">Nenhum pedido encontrado.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.code}`} {...swipe.handlers(o.id)} style={swipe.rowStyle(o.id)}
                onClick={() => openPreview(o)} title="Clique para ver a prévia do pedido"
                className="border-b border-zinc-800/60 hover:bg-zinc-900 transition-colors cursor-pointer">
                <td className="px-3 py-3 w-[44px] min-w-[44px] sticky-col-l" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" data-testid={`select-order-${o.code}`} checked={selected.has(o.id)}
                    onChange={() => toggleSelect(o.id)}
                    className="accent-indigo-500 w-4 h-4 cursor-pointer align-middle" />
                </td>
                <td className="px-4 py-3 text-zinc-500 sticky-col-l2">#{o.code}</td>
                <td className="px-4 py-3 text-zinc-200 font-medium align-top">{o.client_name}
                  {!isVendedor && o.items?.some((it) => it.internal) && (
                    <span data-testid={`order-internal-badge-${o.code}`} className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-violet-950/60 text-violet-300 border border-violet-800/60 rounded px-1.5 py-0.5 align-middle">Interno</span>
                  )}
                  <OrderItemsSummary order={o} isVendedor={isVendedor} max={2} expanded={expandedItems.has(o.id)} onToggle={() => toggleItems(o.id)} />
                </td>
                <td className="px-4 py-3 text-zinc-400" data-testid={`order-vendedor-${o.code}`}>{o.vendedor_name || "—"}</td>
                <td className="px-4 py-3 font-medium">{fmtBRL(o.total_value)}</td>
                {!isVendedor && (
                  <td className="px-4 py-3" data-testid={`order-margin-${o.code}`}>
                    {o.cmv_total != null ? (
                      <span className={`font-medium ${o.margin_value >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {fmtBRL(o.margin_value)}
                        <span className="block text-[10px] font-normal text-zinc-500">
                          {o.total_value > 0 ? `${Math.round((o.margin_value / o.total_value) * 100)}%` : "—"} · CMV {fmtBRL(o.cmv_total)}
                        </span>
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                )}
                <td className="px-4 py-3 text-zinc-400">{o.nf_number || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{fmtDate(o.order_date)}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-4 py-3 text-zinc-400">
                  {o.payment_method}
                  {o.payment_method === "Boleto" && o.boleto_days > 0 && (
                    <span data-testid={`order-boleto-days-${o.code}`} className="text-amber-400"> · {o.boleto_days} dias</span>
                  )}
                  {o.payment_method === "Boleto" && user?.role === "admin" && (
                    <button data-testid={`order-boleto-sent-toggle-${o.code}`}
                      onClick={(e) => { e.stopPropagation(); toggleBoletoSent(o); }}
                      title={o.boleto_sent ? "Você marcou este boleto como enviado — clique para desmarcar" : "Marcar que VOCÊ já enviou o boleto ao cliente (o sistema não envia nada)"}
                      className={`block mt-1 text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 border transition-colors ${o.boleto_sent ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20" : "text-amber-400 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20"}`}>
                      {o.boleto_sent ? "Boleto enviado ✓" : "Boleto não enviado"}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-zinc-400">{o.installments}x{o.entrada_value > 0 && <span data-testid={`order-entrada-badge-${o.code}`} className="block text-[10px] text-amber-400">+ entrada {fmtBRL(o.entrada_value)} ({o.entrada_method})</span>}</td>
                <td className="px-4 py-3"><PayStatus o={o} testId={`order-pay-status-${o.code}`} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap sticky-col-r" onClick={(e) => e.stopPropagation()}>
                  {!isVendedor && (
                    <button data-testid={`payments-order-${o.code}`} onClick={() => setPayOrder(o)} title="Histórico de Pagamentos"
                      className="text-zinc-400 hover:text-amber-400 p-1 transition-colors"><Wallet size={16} /></button>
                  )}
                  <button data-testid={`signature-order-${o.code}`} onClick={() => setSignOrder(o)}
                    title={o.has_signature ? "Ver assinatura de recebimento" : "Coletar assinatura do cliente"}
                    className={`p-1 ml-1 transition-colors ${o.has_signature ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-400 hover:text-indigo-400"}`}>
                    <PenNib size={16} weight={o.has_signature ? "fill" : "regular"} />
                  </button>
                  {!isVendedor && o.payment_method === "Boleto" && o.payment_status !== "Pago" && (
                    <button data-testid={`boletos-order-${o.code}`} onClick={() => setBoletoOrder(o)} title="Emitir boletos Sicredi das parcelas em aberto"
                      className="text-zinc-400 hover:text-emerald-400 p-1 ml-1 transition-colors"><Receipt size={16} /></button>
                  )}
                  {!isVendedor && (
                    <button data-testid={`nfe-order-${o.code}`} onClick={() => setNfeOrder(o)} title="NF-e / DANFE"
                      className="text-zinc-400 hover:text-sky-400 p-1 ml-1 transition-colors"><Barcode size={16} /></button>
                  )}
                  <button data-testid={`print-order-logo-${o.code}`} onClick={() => printPdf(o, true)} title="PDF com logotipo"
                    className="text-zinc-400 hover:text-amber-400 p-1 ml-1 transition-colors"><ImageSquare size={16} /></button>
                  <button data-testid={`print-order-${o.code}`} onClick={() => printPdf(o)} title="Imprimir talão (PDF)"
                    className="text-zinc-400 hover:text-emerald-400 p-1 ml-1 transition-colors"><Printer size={16} /></button>
                  <button data-testid={`edit-order-${o.code}`} onClick={() => openEdit(o)} className="text-zinc-400 hover:text-indigo-400 p-1 ml-1 transition-colors"><PencilSimple size={16} /></button>
                  <button data-testid={`delete-order-${o.code}`} onClick={() => remove(o.id)} className="text-zinc-400 hover:text-rose-400 p-1 ml-1 transition-colors"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onChange={setPage} testId="orders-pagination" label="pedido(s)" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-3xl max-h-[94dvh] sm:max-h-[92vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar Pedido" : "Novo Pedido"}</DialogTitle>
          </DialogHeader>
          <form ref={formRef} onSubmit={save} className="space-y-4">
            <ZeroCostBanner products={zeroCostProducts} />
            <div data-testid="order-slip" style={{ colorScheme: "light" }} className="bg-slate-100 rounded-md overflow-hidden border border-slate-300 shadow-inner">
              {/* Cabeçalho do talão */}
              <div className="bg-blue-950 text-white px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <span className="font-display font-semibold tracking-wide text-sm">
                  PEDIDO Nº{" "}
                  <span data-testid="order-slip-number" className="bg-white/10 border border-white/20 rounded px-2 py-0.5 ml-1">
                    {editing ? `#${editingCode}` : "AUTOMÁTICO"}
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide">Data</span>
                  <DatePicker light testId="order-date-input" value={form.order_date}
                    onChange={(v) => setForm({ ...form, order_date: v })} />
                </div>
              </div>
              <div className="divide-y divide-slate-300">
                <div className="px-4 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 bg-slate-200">
                  <span className={`${pLabel} sm:w-20`}>Cliente</span>
                  <ClientSearchInput testId="order-client-select" className="w-full sm:flex-1" variant="light" clients={clients}
                    value={form.client_id}
                    onChange={(id) => {
                      const c = clients.find((x) => x.id === id);
                      setForm({ ...form, client_id: id, document: form.document || c?.document || "" });
                    }} />
                </div>
                <div className="px-4 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 bg-slate-100">
                  <span className={`${pLabel} sm:w-20`}>Endereço</span>
                  <input data-testid="order-address-input" className={pInput} value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rua, número, bairro" />
                </div>
                <div className="px-4 py-2.5 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px] bg-slate-200">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <span className={`${pLabel} sm:w-20`}>Cidade</span>
                    <input data-testid="order-city-input" className={pInput} value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                    <span className={pLabel}>Est.</span>
                    <input data-testid="order-state-input" className={pInput} maxLength={2} value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} placeholder="SP" />
                  </div>
                </div>
                <div className="px-4 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 bg-slate-100">
                  <span className={`${pLabel} sm:w-20`}>CPF / CNPJ</span>
                  <input data-testid="order-document-input" className={pInput} value={form.document} maxLength={18}
                    onChange={(e) => setForm({ ...form, document: maskDocument(e.target.value) })} placeholder="000.000.000-00 ou 00.000.000/0000-00" />
                </div>
                <div className="px-4 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3 bg-slate-200">
                  <span className={`${pLabel} sm:w-20 sm:pt-1.5`}>Observações</span>
                  <textarea data-testid="order-notes-input" className={`${pInput} min-h-[56px] resize-y`} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anotações do pedido (aparecem no PDF)" />
                </div>
              </div>
              {/* Itens — cards empilhados (mobile / retrato) */}
              <div className="sm:hidden border-t border-slate-300">
                <div className="bg-blue-950 text-white text-[11px] uppercase tracking-wide px-4 py-2 font-semibold">
                  Itens do Pedido
                </div>
                <div className="divide-y divide-slate-300">
                  {form.items.map((it, idx) => (
                    <div key={it._key || idx} data-testid={`item-card-${idx}`}
                      className={`p-3 space-y-3 ${it.internal ? "bg-violet-100" : idx % 2 === 0 ? "bg-slate-100" : "bg-slate-200"}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-blue-950">Item {idx + 1}</span>
                        {form.items.length > 1 && (
                          <button type="button" data-testid={`item-card-remove-${idx}`} onClick={() => removeItem(idx)}
                            className="text-slate-400 hover:text-rose-600 p-2 -m-1 transition-colors"><Trash size={18} /></button>
                        )}
                      </div>
                      <div>
                        <span className={`${pLabel} block mb-1`}>Descrição / Produto</span>
                        <ProductSearchInput testId={`item-card-description-${idx}`} descIdx={idx} products={products}
                          className={`${pInput} ${it.product_id ? "ring-1 ring-emerald-600/60" : ""}`}
                          value={it.description}
                          linkedLabel={it.product_id ? (products.find((p) => p.id === it.product_id)?.name || "produto vinculado") : null}
                          onUnlink={() => setItemDesc(idx, it.description, null)}
                          onChange={(v) => setItem(idx, "description", v)}
                          onPick={(p) => setItemDesc(idx, p.name, p.id)} />
                        {(() => { const vp = it.product_id && products.find((p) => p.id === it.product_id); return vp && vp.variation_alert_active ? (
                          <p data-testid={`item-card-variation-warning-${idx}`} className="mt-1 flex items-start gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1">
                            <WarningCircle size={13} className="shrink-0 mt-[1px]" />
                            <span><b>Aviso interno</b> (não sai no PDF): {vp.variation_note || "variação de medida no lote atual"}</span>
                          </p>
                        ) : null; })()}
                        {(() => { const zp = it.product_id && products.find((p) => p.id === it.product_id); return zp && zp.has_cost === false ? (
                          <ZeroCostItemWarning testId={`item-card-zero-cost-${idx}`} product={zp}
                            canFix={!isVendedor} onFix={() => setCostProduct(zp)} />
                        ) : null; })()}
                        <label className="mt-1.5 flex items-center gap-1.5 cursor-pointer select-none w-fit">
                          <input type="checkbox" data-testid={`item-card-internal-toggle-${idx}`} className="accent-violet-700 w-4 h-4"
                            checked={!!it.internal} onChange={(e) => toggleInternal(idx, e.target.checked)} />
                          <span className={`text-[11px] font-semibold ${it.internal ? "text-violet-800" : "text-slate-500"}`}>
                            Item interno — soma no total, baixa estoque, não sai no PDF
                          </span>
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className={`${pLabel} block mb-1`}>Quant</span>
                          <input data-testid={`item-card-quant-${idx}`} type="number" min="0" step="any" className={`${pInput} text-center`}
                            value={it.quant} onChange={(e) => setItem(idx, "quant", e.target.value)} />
                        </div>
                        <div>
                          <span className={`${pLabel} block mb-1`}>Unitário (R$)</span>
                          <input data-testid={`item-card-unit-price-${idx}`} type="number" min="0" step="0.01"
                            className={`${pInput} text-right`}
                            value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between bg-white border border-slate-300 rounded px-3 py-2">
                        <span className={pLabel}>Total do item</span>
                        <span data-testid={`item-card-total-${idx}`} className="font-semibold text-slate-900">{fmtBRL(itemTotal(it))}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Tabela de itens (desktop / paisagem) */}
              <table className="w-full text-sm border-t border-slate-300 hidden sm:table">
                <thead>
                  <tr className="bg-blue-950 text-white text-[11px] uppercase tracking-wide">
                    <th className="px-3 py-2 font-semibold text-left w-20">Quant</th>
                    <th className="px-3 py-2 font-semibold text-left">Descrição / Produto</th>
                    <th className="px-3 py-2 font-semibold text-right w-32">Unitário (R$)</th>
                    <th className="px-3 py-2 font-semibold text-right w-32">Total (R$)</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => (
                    <tr key={it._key || idx} className={it.internal ? "bg-violet-100" : idx % 2 === 0 ? "bg-slate-100" : "bg-slate-200"}>
                      <td className="px-2 py-1.5">
                        <input data-testid={`item-quant-${idx}`} type="number" min="0" step="any" className={`${pInput} text-center`}
                          value={it.quant} onChange={(e) => setItem(idx, "quant", e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <ProductSearchInput testId={`item-description-${idx}`} descIdx={idx} products={products}
                          className={`${pInput} ${it.product_id ? "ring-1 ring-emerald-600/60" : ""}`}
                          value={it.description}
                          linkedLabel={it.product_id ? (products.find((p) => p.id === it.product_id)?.name || "produto vinculado") : null}
                          onUnlink={() => setItemDesc(idx, it.description, null)}
                          onChange={(v) => setItem(idx, "description", v)}
                          onPick={(p) => setItemDesc(idx, p.name, p.id)} />
                        {(() => { const vp = it.product_id && products.find((p) => p.id === it.product_id); return vp && vp.variation_alert_active ? (
                          <p data-testid={`item-variation-warning-${idx}`} className="mt-1 flex items-start gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1">
                            <WarningCircle size={13} className="shrink-0 mt-[1px]" />
                            <span><b>Aviso interno</b> (não sai no PDF): {vp.variation_note || "variação de medida no lote atual"}</span>
                          </p>
                        ) : null; })()}
                        {(() => { const zp = it.product_id && products.find((p) => p.id === it.product_id); return zp && zp.has_cost === false ? (
                          <ZeroCostItemWarning testId={`item-zero-cost-${idx}`} product={zp}
                            canFix={!isVendedor} onFix={() => setCostProduct(zp)} />
                        ) : null; })()}
                        <label className="mt-1 flex items-center gap-1.5 cursor-pointer select-none w-fit">
                          <input type="checkbox" data-testid={`item-internal-toggle-${idx}`} className="accent-violet-700 w-3.5 h-3.5"
                            checked={!!it.internal} onChange={(e) => toggleInternal(idx, e.target.checked)} />
                          <span className={`text-[11px] font-semibold ${it.internal ? "text-violet-800" : "text-slate-500"}`}>
                            Item interno — soma no total, baixa estoque, não sai no PDF
                          </span>
                        </label>
                      </td>
                      <td className="px-2 py-1.5">
                        <input data-testid={`item-unit-price-${idx}`} type="number" min="0" step="0.01"
                          className={`${pInput} text-right`}
                          value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} />
                      </td>
                      <td data-testid={`item-total-${idx}`} className="px-3 py-1.5 text-right font-semibold text-slate-900">
                        {fmtBRL(itemTotal(it))}
                      </td>
                      <td className="px-1 py-1.5 text-center">
                        {form.items.length > 1 && (
                          <button type="button" data-testid={`remove-item-${idx}`} onClick={() => removeItem(idx)}
                            className="text-slate-400 hover:text-rose-600 transition-colors"><Trash size={15} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 bg-slate-100 border-t border-slate-300">
                <button type="button" data-testid="add-item-button" onClick={addItem}
                  className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 text-sm sm:text-xs font-semibold text-blue-950 hover:text-blue-700 border border-dashed border-blue-950/40 sm:border-0 rounded py-2.5 sm:py-0 transition-colors">
                  <Plus size={16} weight="bold" /> Adicionar item
                </button>
              </div>
              {/* Rodapé do talão */}
              <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-slate-300 bg-slate-100">
                <div className="px-4 pt-8 pb-4 flex items-end order-2 sm:order-1 sm:pt-12">
                  <div className="border-t border-slate-500 w-52 text-center text-xs text-slate-600 pt-1">Visto</div>
                </div>
                <div className="p-4 flex items-center justify-end order-1 sm:order-2">
                  <div className="flex flex-col items-stretch gap-2 w-full sm:w-auto sm:items-end">
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <span className={pLabel}>Subtotal</span>
                      <span data-testid="order-subtotal" className="text-sm font-semibold text-slate-900 w-32 text-right">{fmtBRL(subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <span className={pLabel}>Desconto (R$)</span>
                      <input data-testid="order-discount-input" type="number" min="0" step="0.01" max={subtotal || undefined}
                        className={`${pInput} text-right w-32`} placeholder="0,00" value={form.discount}
                        onChange={(e) => setForm({ ...form, discount: e.target.value })} />
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <span className={pLabel}>Frete (R$)</span>
                      <input data-testid="order-freight-input" type="number" min="0" step="0.01"
                        className={`${pInput} text-right w-32`} placeholder="0,00" value={form.freight}
                        onChange={(e) => setForm({ ...form, freight: e.target.value })} />
                    </div>
                    <div className="bg-blue-950 text-white rounded px-5 py-3 text-right w-full sm:w-auto">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-blue-200">Total Geral do Pedido</p>
                      <p data-testid="order-grand-total" className="font-display text-2xl font-semibold">{fmtBRL(grandTotal)}</p>
                      {discountVal > 0 && (
                        <p data-testid="order-discount-note" className="text-[10px] text-emerald-300 mt-0.5">desconto de {fmtBRL(discountVal)} aplicado</p>
                      )}
                      {freightVal > 0 && (
                        <p data-testid="order-freight-note" className="text-[10px] text-sky-300 mt-0.5">frete de {fmtBRL(freightVal)} incluído</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-slate-200 border-t border-slate-300 px-4 py-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <span className={`${pLabel} block mb-1`}>Nº NF / Pedido Ext.</span>
                  <input data-testid="order-nf-input" className={pInput} value={form.nf_number}
                    onChange={(e) => setForm({ ...form, nf_number: e.target.value })} />
                </div>
                <div>
                  <span className={`${pLabel} block mb-1`}>Forma de Pagamento</span>
                  <select data-testid="order-payment-select" className={pInput} value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                    {PAYMENT_METHODS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                {form.payment_method === "Boleto" && (
                  <div>
                    <span className={`${pLabel} block mb-1`}>Prazo do Boleto (dias)</span>
                    <input data-testid="order-boleto-days-input" type="number" min="1" max="365" className={pInput}
                      placeholder="Ex: 7" value={form.boleto_days}
                      onChange={(e) => setForm({ ...form, boleto_days: e.target.value })} />
                  </div>
                )}
                <div>
                  <span className={`${pLabel} block mb-1`}>{plan.entrada > 0 ? "Parcelas do Restante" : "Qtd. Parcelas"}</span>
                  <input data-testid="order-installments-input" type="number" min="1" max="48" className={pInput}
                    value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} required />
                </div>
                <div>
                  <span className={`${pLabel} block mb-1`}>Comissão (%)</span>
                  <select data-testid="order-commission-select" className={pInput} value={form.commission_percent}
                    onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}>
                    <option value="0">Sem comissão</option>
                    <option value="1">1%</option>
                    <option value="2">2%</option>
                    <option value="2.5">2,5%</option>
                    <option value="5">5%</option>
                  </select>
                </div>
                <div>
                  <span className={`${pLabel} block mb-1`}>Status do Pagamento</span>
                  <div data-testid="order-payment-status-display"
                    className="w-full bg-slate-50 border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
                    {form.payment_status || "Pendente"} <span className="text-slate-400 text-xs">(automático)</span>
                  </div>
                </div>
              </div>
              <OrderPaymentPlan form={form} setForm={setForm} plan={plan} />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <select data-testid="order-status-select" className={`${input} w-full sm:max-w-[220px]`} value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })} title="Status do Pedido">
                {ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <p className="text-xs text-zinc-500 flex-1">
                Automação: ao salvar, serão gerados lançamentos de <span className="text-emerald-400">Recebimento</span> (1 por parcela{plan.entrada > 0 ? " — a entrada já entra como paga" : ""}). Entrada + parcelas sempre fecham com o total.
              </p>
            </div>
            <button data-testid="save-order-button" disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3.5 sm:py-2.5 text-base sm:text-sm rounded transition-colors">
              {saving ? (<><CircleNotch size={18} className="animate-spin" /> Salvando pedido...</>) : "Salvar Pedido"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <OrderPaymentsDialog order={payOrder} onClose={() => setPayOrder(null)} onChanged={load} />
      <SignatureDialog order={signOrder} onClose={() => setSignOrder(null)} onSaved={load} />
      <DocumentPreviewDialog doc={previewOrder} type="order" onClose={() => setPreviewOrder(null)} />
      <OrderCreatedDialog info={created} onClose={() => setCreated(null)} />
      <DuplicateOrdersDialog open={dupOpen} onClose={() => setDupOpen(false)} onChanged={load} />
      {nfeOrder && <NfeDialog order={nfeOrder} onClose={() => setNfeOrder(null)} />}
      <EmitBoletoDialog open={!!boletoOrder} onClose={() => setBoletoOrder(null)} mode="order" target={boletoOrder} onDone={load} />
      {costProduct && <QuickCostDialog key={costProduct.id} product={costProduct}
        onClose={() => setCostProduct(null)} onSaved={onCostSaved} />}
    </div>
  );
}
